import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Product,
  ProductsResponse,
  ProductFilters,
  defaultProductFilters
} from '../types/product';
import productService from '../services/api/productService';

export interface UseProductsResult {
  products: Product[];
  pagination: ProductsResponse['meta'] | null;
  links: ProductsResponse['links'] | null;
  filters: ProductFilters;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  updateFilters: (newFilters: Partial<ProductFilters>) => void;
  resetFilters: () => void;
  refreshProducts: (opts?: { silent?: boolean }) => Promise<void>;
  loadProducts: (filters?: ProductFilters) => Promise<void>;
  clearError: () => void;
}

export const useProducts = (
  initialFilters: Partial<ProductFilters> = {},
  autoLoad: boolean = true
): UseProductsResult => {
  // State management
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<ProductsResponse['meta'] | null>(null);
  const [links, setLinks] = useState<ProductsResponse['links'] | null>(null);
  const [filters, setFilters] = useState<ProductFilters>({
    ...defaultProductFilters,
    ...initialFilters
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>(initialFilters.search || '');

  // 一覧の軽量シグネチャ（件数 + 各商品の id/status/updated_at）。
  // ポーリング再取得で内容が変わっていない時に setState を抑止し、再レンダー・
  // スクロール/選択リセット・スケルトンのチラつきを防ぐ。
  const signatureRef = useRef<string | null>(null);

  const computeSignature = (list: Product[]): string =>
    `${list.length}|` +
    list.map((p) => `${p.id}:${p.status}:${p.updated_at ?? ''}`).join(',');

  /**
   * Load products with current filters.
   * @param opts.silent true のときローディング表示を出さず、内容に変化がある時だけ state を更新する（ポーリング用）。
   */
  const loadProducts = useCallback(async (customFilters?: ProductFilters, opts?: { silent?: boolean }): Promise<void> => {
    const silent = opts?.silent ?? false;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const filtersToUse = customFilters || filters;
      if (!silent) {
        console.log('📊 useProducts: Loading products with filters:', filtersToUse);
      }

      const response = await productService.getProducts(filtersToUse);

      // 内容が前回と同一なら state を更新しない（参照が変わらず再レンダーを避ける）。
      const signature = computeSignature(response.data);
      if (signature !== signatureRef.current) {
        signatureRef.current = signature;
        setProducts(response.data);
        setPagination(response.meta);
        setLinks(response.links);
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Product一覧の取得に失敗しました';
      console.error('❌ useProducts: Failed to load products:', errorMessage);
      // サイレント（ポーリング）中の失敗は現状の一覧を保持し、次回で回復を試みる。
      if (!silent) {
        setError(errorMessage);
        setProducts([]);
        setPagination(null);
        setLinks(null);
        signatureRef.current = null;
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [filters]);

  /**
   * Refresh products with current filters.
   * @param opts.silent true のときポーリング用のサイレント再取得（ローディング非表示・差分時のみ更新）。
   */
  const refreshProducts = useCallback(async (opts?: { silent?: boolean }): Promise<void> => {
    await loadProducts(undefined, opts);
  }, [loadProducts]);

  /**
   * Update filters and reload products
   */
  const updateFilters = useCallback((newFilters: Partial<ProductFilters>) => {
    console.log('🔄 useProducts: Updating filters:', newFilters);

    const updatedFilters = {
      ...filters,
      ...newFilters,
      // Reset to page 1 when filters change (except when explicitly setting page)
      page: newFilters.page !== undefined ? newFilters.page : 1
    };

    setFilters(updatedFilters);
  }, [filters]);

  /**
   * Reset filters to default values
   */
  const resetFilters = useCallback(() => {
    console.log('🔄 useProducts: Resetting filters to default');
    const resetFilters = {
      ...defaultProductFilters,
      ...initialFilters
    };
    setFilters(resetFilters);
    setSearchQuery(initialFilters.search || '');
  }, [initialFilters]);

  /**
   * Update search query and apply search filter
   */
  const handleSetSearchQuery = useCallback((query: string) => {
    console.log('🔍 useProducts: Setting search query:', query);
    setSearchQuery(query);
    updateFilters({ search: query, page: 1 });
  }, [updateFilters]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-load products when filters change
  useEffect(() => {
    if (autoLoad) {
      loadProducts();
    }
  }, [filters, autoLoad, loadProducts]);

  // Initial load
  useEffect(() => {
    if (autoLoad) {
      console.log('🚀 useProducts: Initial load with auto-load enabled');
      loadProducts();
    }
  }, []); // Only run on mount

  return {
    products,
    pagination,
    links,
    filters,
    loading,
    error,
    searchQuery,
    setSearchQuery: handleSetSearchQuery,
    updateFilters,
    resetFilters,
    refreshProducts,
    loadProducts,
    clearError
  };
};